import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { PERFORM } from "../lib/constants";
import { trackPerformSubmit } from "../lib/track";

/**
 * Open call application form — dancers apply to perform at the Chandigarh
 * edition.
 *
 * Same Netlify Forms contract as InterestForm: the submission is url-encoded
 * and POSTed to the static stub in public/__forms.html, which is what registers
 * this form at deploy time. `form-name` routes it to the right form in the
 * dashboard; without it Netlify returns 404. The field names here ARE the
 * contract — rename one without mirroring it in the stub and that field
 * silently stops arriving. scripts/verify-content.js asserts the two stay in
 * step and fails the build if they drift.
 *
 * videoUrl is a link, not an upload: performers already host their work, and a
 * file field would mean hosting video we do not need to own.
 */
const FIELDS = {
  name: "", email: "", phone: "", city: "",
  actType: "Solo", groupSize: "", style: "", videoUrl: "",
  instagram: "", note: "",
};

export default function PerformForm() {
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [form, setForm] = useState(FIELDS);
  // Honeypot — hidden from people, irresistible to bots. Netlify drops any
  // submission where it is filled. Name must match data-netlify-honeypot.
  const [botField, setBotField] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "perform",
          "bot-field": botField,
          name: form.name,
          email: form.email,
          phone: form.phone,
          city: form.city,
          actType: form.actType,
          groupSize: form.groupSize,
          style: form.style,
          videoUrl: form.videoUrl,
          instagram: form.instagram,
          note: form.note,
        }).toString(),
      });
      if (!res.ok) throw new Error("That didn't go through.");
      trackPerformSubmit(form.actType);
      setStatus("done");
    } catch (err) {
      // Never pretend a dropped application succeeded — they filmed something.
      setError(err.message || "That didn't go through.");
      setStatus("error");
    }
  }

  const labelCls =
    "block font-body text-xs tracking-[0.14em] uppercase mb-2 text-[#C9A961]";
  const fieldCls =
    "w-full rounded-xl px-4 py-3 font-body text-base outline-none transition border " +
    "bg-[#1A1410] border-[#C9A961]/35 text-[#F5EFE6] placeholder:text-[#E8D9C5]/40 focus:border-[#C9A961]";

  if (status === "done") {
    return (
      <div
        className="max-w-xl mx-auto text-center rounded-2xl border border-[#C9A961]/35 bg-[#1A1410] p-10"
        data-testid="perform-done"
      >
        <CheckCircle2 size={38} strokeWidth={1.3} className="mx-auto text-[#C9A961]" />
        <h3 className="mt-5 font-display text-3xl text-[#F5EFE6]">Received.</h3>
        <p className="mt-3 font-body text-base text-[#E8D9C5]/85 leading-relaxed">
          Your application has reached us. We watch every video ourselves, and
          we will be in touch directly once the shortlist is made.
        </p>
        <p className="mt-4 font-body text-sm text-[#E8D9C5]/60">
          Applications are open until {PERFORM.deadlineLabel}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="max-w-2xl mx-auto"
      data-testid="perform-form"
      name="perform"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      <input type="hidden" name="form-name" value="perform" />
      {/* honeypot — visually hidden, not display:none, so bots still fill it */}
      <p className="absolute left-[-9999px]" aria-hidden="true">
        <label>
          Leave this empty
          <input
            type="text"
            name="bot-field"
            tabIndex={-1}
            autoComplete="off"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
          />
        </label>
      </p>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="pf-name">Name</label>
          <input id="pf-name" name="name" required className={fieldCls}
            value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="pf-city">City</label>
          <input id="pf-city" name="city" required className={fieldCls}
            value={form.city} onChange={set("city")} placeholder="Chandigarh, Delhi..." />
        </div>
        <div>
          <label className={labelCls} htmlFor="pf-phone">Phone</label>
          <input id="pf-phone" name="phone" type="tel" required className={fieldCls}
            value={form.phone} onChange={set("phone")} placeholder="WhatsApp number" />
        </div>
        <div>
          <label className={labelCls} htmlFor="pf-email">Email</label>
          <input id="pf-email" name="email" type="email" required className={fieldCls}
            value={form.email} onChange={set("email")} placeholder="you@example.com" />
        </div>
        <div>
          <label className={labelCls} htmlFor="pf-acttype">Solo or group</label>
          <select id="pf-acttype" name="actType" className={fieldCls}
            value={form.actType} onChange={set("actType")}>
            <option>Solo</option>
            <option>Group</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="pf-groupsize">How many of you</label>
          <input id="pf-groupsize" name="groupSize" className={fieldCls}
            value={form.groupSize} onChange={set("groupSize")}
            placeholder="If it is a group, how many. Otherwise 1" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="pf-style">Dance style</label>
          <input id="pf-style" name="style" required className={fieldCls}
            value={form.style} onChange={set("style")}
            placeholder="Kathak, Bharatanatyam, Odissi, folk, semi-classical..." />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="pf-video">Video link</label>
          <input id="pf-video" name="videoUrl" type="url" required className={fieldCls}
            value={form.videoUrl} onChange={set("videoUrl")}
            placeholder="YouTube, Drive or Instagram link" />
          <p className="mt-2 font-body text-xs text-[#E8D9C5]/55">
            Under two minutes. A rehearsal clip is perfectly good. Please keep
            the link public, or we will not be able to watch it.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="pf-insta">Instagram</label>
          <input id="pf-insta" name="instagram" className={fieldCls}
            value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="pf-note">Anything else</label>
          <textarea id="pf-note" name="note" rows={3} className={fieldCls + " resize-y"}
            value={form.note} onChange={set("note")}
            placeholder="Optional — where you trained, where you have performed, anything you would like us to know" />
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          type="submit"
          disabled={status === "sending"}
          className="cta-primary group disabled:opacity-60"
          data-testid="perform-submit"
        >
          <Send size={19} strokeWidth={1.6} />
          <span>{status === "sending" ? "Sending..." : "Send application"}</span>
        </button>
        {status === "error" && (
          <p className="mt-4 font-body text-sm text-[#E8B4A0]" role="alert">
            {error} Please try again, or send it to hello@kashiwithanshu.com.
          </p>
        )}
      </div>
    </form>
  );
}
